/**
 * WEB-10: сколько сообщений ждёт владельца — и кто вошёл в чат по его ссылке.
 *
 * 🔴 Зачем. До этого узнать о новом сообщении можно было ровно одним способом:
 * самому открыть страницу «Мой QR» — значок «2 новых» рисовался ТОЛЬКО там.
 * Ни в меню, ни в шапке, ни уведомлением ничего не появлялось, и владелец
 * 31.08 спросил прямым текстом: «человек зашёл в чат — а мне-то как туда
 * попасть?». Отсюда счётчик в шапке кабинета (виден с любой страницы) и
 * уведомление браузера.
 *
 * Источник — та же ручка, что у страницы «Мой QR»: `GET /msg/members` отдаёт
 * `unread` по каждому участнику, считая его ПО МЕТАДАННЫМ, без крипты. Своей
 * ручки заводить не пришлось.
 *
 * 🔴 WEB-12: сюда же входит ОБЩАЯ КОМНАТА. Её сообщения живут отдельно от
 * переписки 1:1 (`msg_room_envelopes`, а не `msg_envelopes`), и в `/msg/members`
 * их нет вовсе — то есть владелец, стоящий на карте или в настройках, не узнал
 * бы о них ничем. Это та же болезнь, что у участника (тикет WEB-12), только с
 * другой стороны: сервер всё доставил, человек не узнал. Число даёт та же
 * ручка `GET /msg/rooms`, считая его по метаданным.
 *
 * 🔴 Чего этот счётчик НЕ покрывает: переписку с СОТРУДНИКАМИ (маршрут
 * /msg/:userId). Ручки «непрочитанное по всем разговорам» на сервере нет, а
 * заводить её — отдельная работа с выкаткой. Здесь честно считаются участники
 * по приглашению и общая комната.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { listMembers } from './inviteApi';
import { MsgApi } from './msgApi';
import { browserDeviceId } from './deviceId';
import { useAuth } from '../context/AuthContext';
import { logger } from '../utils/logger';

/**
 * Своя комната глазами ЭТОГО браузера.
 *
 * 🔴 X-Device-Id тут несущий: `unread` сервер считает по курсору УСТРОЙСТВА, и
 * без заголовка он ответил бы «всё непрочитано с момента входа».
 */
const listOwnRooms = () => new MsgApi(browserDeviceId()).listRooms();

/** Как часто спрашиваем сервер, пока вкладка открыта. */
export const POLL_MS = 30_000;

/**
 * Окно, в котором ОДНО И ТО ЖЕ событие показывается один раз.
 *
 * 🔴 Вкладок кабинета у человека может быть несколько, и каждая опрашивает
 * сервер сама. Без общей отметки в localStorage он получил бы столько
 * уведомлений, сколько вкладок открыл.
 */
export const NOTIFY_DEDUPE_MS = 60_000;

const NOTIFY_KEY = 'msg-last-notification';

/**
 * Что изменилось между двумя опросами.
 *
 * Вход — списки участников с сервера; выход — только события, о которых стоит
 * говорить человеку. Первый опрос событий не даёт вовсе: иначе при каждом
 * открытии кабинета сыпались бы уведомления обо всём, что накопилось.
 *
 * @param prev Map(id -> unread) прошлого опроса или null, если его не было
 * @param next массив участников из ответа сервера
 */
export function diffMembers(prev, next) {
  if (!prev) return { grown: [], joined: [] };
  const grown = [];
  const joined = [];
  for (const m of next) {
    const before = prev.get(m.id);
    if (before === undefined) {
      joined.push(m);
      // 🔴 Новичок с непрочитанным — это ОДНО событие «вошёл», а не два:
      // человек и так откроет чат, а два уведомления подряд про одного и того
      // же читаются как сбой.
      continue;
    }
    if ((m.unread ?? 0) > before) grown.push(m);
  }
  return { grown, joined };
}

/** Снимок «сколько было непрочитанного у кого» для следующего сравнения. */
export function snapshot(members) {
  return new Map(members.map((m) => [m.id, m.unread ?? 0]));
}

export function totalUnread(members) {
  return members.reduce((sum, m) => sum + (m.unread ?? 0), 0);
}

/**
 * Показать уведомление, если такое же не показала соседняя вкладка.
 * @returns было ли показано (для тестов и для отладки)
 */
export function notifyOnce(key, title, body, now = Date.now()) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false;
  }
  try {
    // 🔴 КАРТА событий, а не одно последнее (ревью WEB-10 р.1, Medium): за один
    // опрос событий бывает несколько, и «последний ключ» затирался вторым — а
    // соседняя вкладка тогда честно показывала оба заново. Заодно чистим
    // просроченные, чтобы карта не росла вечно.
    const raw = JSON.parse(localStorage.getItem(NOTIFY_KEY) ?? '{}');
    const map = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
    // 🔴 Свежей считается отметка из ПРОШЛОГО и не старше окна (ревью р.2,
    // Low): битое или «будущее» значение в localStorage иначе глушило бы это
    // уведомление навсегда.
    const isFresh = (at) => Number.isFinite(at) && at <= now
      && now - at < NOTIFY_DEDUPE_MS;
    if (isFresh(map[key])) return false;
    const fresh = {};
    for (const [k, at] of Object.entries(map)) {
      if (isFresh(at)) fresh[k] = at;
    }
    fresh[key] = now;
    localStorage.setItem(NOTIFY_KEY, JSON.stringify(fresh));
  } catch {
    // localStorage может быть недоступен (приватный режим) — тогда без
    // дедупликации, но с уведомлением: молчать хуже, чем повториться
  }
  try {
    // eslint-disable-next-line no-new
    new Notification(title, { body, tag: key });
    return true;
  } catch (e) {
    logger.warn('WEB-10: уведомление не показалось', e);
    return false;
  }
}

/**
 * Счётчик непрочитанного для шапки кабинета.
 *
 * @param api подменяемые зависимости (тесты): fetchMembers, now
 */
export function useUnread({ fetchMembers = listMembers,
  fetchRooms = listOwnRooms } = {}) {
  const { msgInvites, msgRooms, loaded } = useAuth();
  const [members, setMembers] = useState([]);
  // Общая комната: {id, unread} или null. Отдельным состоянием, а не в списке
  // участников: это ДРУГАЯ сущность, и ведёт она на другой экран.
  const [room, setRoom] = useState(null);
  const prevRoomUnread = useRef(null);
  const roomSeq = useRef(0);
  const [permission, setPermission] = useState(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  const previous = useRef(null);
  // 🔴 Два сторожа (ревью WEB-10 р.1, Medium): `alive` — что вкладка ещё здесь
  // (ответ, прилетевший после ухода, не имеет права ни обновить состояние, ни
  // показать уведомление), `seq` — что ответ не обогнали более свежим. Без
  // второго медленный опрос откатывал бы счётчик и снимок назад.
  const alive = useRef(true);
  const seq = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const poll = useCallback(async () => {
    if (!loaded || !msgInvites) return;
    const mine = seq.current + 1;
    seq.current = mine;
    let list;
    try {
      list = await fetchMembers();
    } catch (e) {
      if (!alive.current || seq.current !== mine) return;
      // Доступ пропал или раздел выключили: держать старый счётчик нельзя —
      // он поведёт человека в чат, которого больше нет (ревью WEB-10 р.1, Low).
      //
      // 🔴 401 фатален СРАЗУ, и это не забывчивость (ревью р.2 предлагал
      // обратное, р.3 и р.4 чинили следствия — все три стояли на неверной
      // посылке «одиночный 401 лечится сам»). Посылка неверна: до нас 401
      // просто не доходит, пока сессию можно спасти. Интерсептор
      // `axiosInstance` обновляет токен и ПОВТОРЯЕТ тот же запрос сам
      // (`axiosInstance.jsx`, ветка `!originalRequest._retry`), и вызывающий
      // видит успех. А если обновить не вышло — или повтор снова получил 401 —
      // интерсептор чистит токены и уводит на `/`. То есть 401 в этом catch
      // означает: сессии больше нет и страница уже уходит. Держать после этого
      // счётчик не за чем. Зависимость закреплена ДВУМЯ тестами в
      // `axiosInstance.test.js`: «401 -> refresh ... retry» держит первую
      // половину (пока сессию можно спасти, вызывающий видит успех), «401
      // после повтора: токены снесены, уходим на /» — вторую. Покраснеет
      // любой из них — сломается и это рассуждение.
      if ([401, 403, 404].includes(e?.status)) {
        previous.current = null;
        setMembers([]);
      }
      // Прочее — молча: счётчик удобство, а не работа. Отказ сети не имеет
      // права рисовать человеку ошибку поверх карты или настроек.
      logger.info('WEB-10: опрос непрочитанного не прошёл', e);
      return;
    }
    if (!alive.current || seq.current !== mine) return;
    const { grown, joined } = diffMembers(previous.current, list);
    previous.current = snapshot(list);
    setMembers(list);
    // 🔴 Уведомляем, только когда вкладка НЕ на виду: если человек смотрит в
    // кабинет, ему уже виден счётчик в шапке, и системное окно поверх него —
    // шум.
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    for (const m of joined) {
      notifyOnce(`joined:${m.id}`, 'Новый участник чата',
        `${m.display_name} вошёл в чат по вашей ссылке`);
    }
    for (const m of grown) {
      notifyOnce(`unread:${m.id}:${m.unread}`, 'Новое сообщение',
        `${m.display_name}: ${m.unread === 1 ? 'новое сообщение' : `${m.unread} новых сообщений`}`);
    }
  }, [fetchMembers, loaded, msgInvites]);

  /**
   * Опрос ОБЩЕЙ КОМНАТЫ. Отдельно от участников намеренно: это разные ручки и
   * разные отказы, и падение одной не имеет права гасить счётчик другой.
   */
  const pollRoom = useCallback(async () => {
    if (!loaded || !msgRooms) return;
    const mine = roomSeq.current + 1;
    roomSeq.current = mine;
    let mineRoom;
    try {
      const answer = await fetchRooms();
      mineRoom = answer?.rooms?.[0] ?? null;
    } catch (e) {
      if (!alive.current || roomSeq.current !== mine) return;
      // Приговор — комнату выключили или доступ пропал: держать счётчик
      // нельзя, он повёл бы в чат, которого нет. Прочее (обрыв сети) молча:
      // счётчик удобство, а не работа.
      if ([401, 403, 404].includes(e?.status)) {
        prevRoomUnread.current = null;
        setRoom(null);
      }
      logger.info('WEB-12: опрос комнаты не прошёл', e);
      return;
    }
    if (!alive.current || roomSeq.current !== mine) return;
    const count = mineRoom?.unread ?? 0;
    const before = prevRoomUnread.current;
    prevRoomUnread.current = count;
    setRoom(mineRoom ? { id: mineRoom.id, unread: count } : null);
    // Первый опрос событий не даёт: иначе открытие кабинета сыпало бы
    // уведомлениями обо всём, что накопилось (то же правило, что у участников).
    if (before === null || count <= before) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    notifyOnce(`room:${count}`, 'Новое в общем чате',
      count === 1 ? 'новое сообщение' : `${count} новых сообщений`);
  }, [fetchRooms, loaded, msgRooms]);

  useEffect(() => {
    if (!loaded || (!msgInvites && !msgRooms)) return undefined;
    const askAll = () => { poll(); pollRoom(); };
    askAll();
    const timer = setInterval(askAll, POLL_MS);
    // Возврат на вкладку — повод спросить сразу, а не ждать конца интервала.
    const onVisible = () => { if (document.visibilityState === 'visible') askAll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      // 🔴 Летящий запрос тоже обесцениваем (ревью р.2, Medium): раздел могли
      // выключить или доступ отобрать, пока ответ был в пути, — и он бы всё
      // равно поставил счётчик и мог показать уведомление.
      seq.current += 1;
      roomSeq.current += 1;
    };
  }, [loaded, msgInvites, msgRooms, poll, pollRoom]);

  /** Спросить разрешение на уведомления. Зовётся ТОЛЬКО из клика человека. */
  const askPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported';
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (e) {
      logger.warn('WEB-10: разрешение на уведомления не получено', e);
      return Notification.permission;
    }
  }, []);

  return {
    enabled: Boolean(loaded && (msgInvites || msgRooms)),
    members,
    room,
    total: totalUnread(members) + (room?.unread ?? 0),
    permission,
    askPermission,
    refresh: () => { poll(); pollRoom(); },
  };
}
